import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { api, errorText } from "../api";
import { Logo } from "../shell";
import type { AuthConfig } from "../types";
import { Banner, Field, type Notice } from "../ui";

/** Why the central login sent the person back (`?login_error=` from the platform's central mode). */
const LOGIN_ERRORS: Record<string, string> = {
  no_access: "Your account has no access to this extranet. Ask your organization's administrator or the Ideastation team.",
  inactive: "Your account is deactivated. Contact the Ideastation team.",
  account_conflict: "An older account with this email has not been migrated yet. Contact the Ideastation team.",
  unknown_organization: "Your organization is not registered in Resosys Access yet. Contact the Ideastation team.",
  access_unavailable: "The access service did not answer. Try again in a moment.",
  mfa_required: "As your organization's administrator, you sign in with two-step verification. Set it up in \"Manage my login\", then sign in again.",
  login_failed: "The sign-in could not be completed. Try again.",
};

/**
 * Login screen: the product's pitch on the left, the form on the right. With the central login there is no
 * form: the page sends the person to Zitadel by itself (with a session there they are back without a click),
 * except after a refusal, whose reason stays on screen.
 */
/** `title` heads the sign-in card; partner extranets keep the default, internal apps name themselves. */
export function LoginPage({ product, headline, tagline, onLoggedIn, auth = { mode: "local" }, title = "Sign in to the extranet" }: { product: string; headline: ReactNode; tagline: ReactNode; onLoggedIn: () => Promise<void>; auth?: AuthConfig; title?: string }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [notice, setNotice] = useState<Notice>();
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setNotice(undefined);
    try {
      await api("POST", "/auth/login", { email, password });
      setPassword("");
      await onLoggedIn();
    } catch (err) {
      setNotice({ tone: "bad", text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };

  const loginError = new URLSearchParams(window.location.search).get("login_error");
  const centralLogin = () => {
    if (auth.mode === "central") window.location.assign(`${auth.loginUrl}?returnTo=${encodeURIComponent(window.location.pathname + window.location.hash)}`);
  };
  useEffect(() => {
    if (auth.mode !== "central" || loginError) return;
    // Never twice within a few seconds, so a broken session cannot loop.
    const key = `autologin:${product}`;
    let last = 0;
    try { last = Number(sessionStorage.getItem(key) ?? 0); sessionStorage.setItem(key, String(Date.now())); } catch { /* no storage */ }
    if (Date.now() - last > 15_000) centralLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.mode, loginError]);

  return (
    <div className="login">
      <div className="hero">
        <Logo />
        <div>
          <h1>{headline}</h1>
          <p style={{ maxWidth: 420, marginTop: 12 }}>{tagline}</p>
        </div>
        <div className="small">{product} · Ideastation</div>
      </div>
      {auth.mode === "central" ? (
        <div className="form">
          <h2 style={{ fontSize: 22 }}>{title}</h2>
          <Banner notice={loginError ? { tone: "bad", text: LOGIN_ERRORS[loginError] ?? LOGIN_ERRORS.login_failed! } : undefined} />
          <button className="primary" type="button" onClick={centralLogin} style={{ justifyContent: "center" }}>Sign in</button>
          {loginError === "mfa_required" && <button type="button" onClick={() => window.open(auth.accountUrl, "_blank", "noopener,noreferrer")} style={{ justifyContent: "center" }}>Manage my login</button>}
          <p className="help">The same login for every Resosys back-office. Ideastation staff sign in with their Google account.</p>
        </div>
      ) : (
      <form className="form" onSubmit={(e) => void submit(e)}>
        <h2 style={{ fontSize: 22 }}>{title}</h2>
        <Banner notice={notice} />
        <Field label="Email"><input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label="Password"><input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
        <button className="primary" type="submit" disabled={busy} style={{ justifyContent: "center" }}>{busy ? "Signing in…" : "Sign in"}</button>
        <p className="help">If you do not have an account or forgot your password, contact the Ideastation team.</p>
      </form>
      )}
    </div>
  );
}
